// ========================================
// COMPOSANT Footer
// Pied de page
// ========================================

import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../../../hooks';
import { 
  FiMail, 
  FiPhone, 
  FiMapPin,
  FiHeart,
  FiCode
} from 'react-icons/fi';
import { 
  FaLinkedin, 
  FaTwitter, 
  FaFacebook, 
  FaInstagram,
  FaWhatsapp,
  FaYoutube,
  FaTiktok
} from 'react-icons/fa';
import './Footer.css';

// ============================================
// MAPPING DES ICÔNES SOCIALES
// ============================================
const SocialIconsMap = {
  linkedin: { icon: FaLinkedin, label: 'LinkedIn' },
  twitter: { icon: FaTwitter, label: 'Twitter' },
  facebook: { icon: FaFacebook, label: 'Facebook' },
  instagram: { icon: FaInstagram, label: 'Instagram' },
  whatsapp: { icon: FaWhatsapp, label: 'WhatsApp' },
  youtube: { icon: FaYoutube, label: 'YouTube' },
  tiktok: { icon: FaTiktok, label: 'TikTok' }
};

const Footer = () => {
  // ========== HOOK FIREBASE ==========
  const { settings } = useSiteSettings();

  // ========== DONNÉES DÉRIVÉES ==========
  const currentYear = new Date().getFullYear();
  
  const siteName = settings?.siteName || 'Gustave KONATE';
  const logoInitials = siteName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  const email = settings?.email || 'contactgkonate@gmail.com';
  const phones = settings?.phones || ['+226 70 11 64 34'];
  const address = settings?.address || 'Ouagadougou, Burkina Faso';
  const tagline = settings?.tagline || 'Communiquer pour transformer, écrire pour inspirer, former pour bâtir l\'avenir.';
  const socialLinks = settings?.socialLinks || {};

  const quickLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/projets', label: 'Projets' },
    { path: '/blog', label: 'Blog' },
    { path: '/a-propos', label: 'À propos' }
  ];

  // Construire les liens sociaux dynamiquement
  const activeSocialLinks = Object.entries(socialLinks)
    .filter(([_, url]) => url && url.trim() !== '')
    .map(([platform, url]) => ({
      platform,
      url,
      ...SocialIconsMap[platform]
    }))
    .filter(link => link.icon);

  return (
    <footer className="main-footer">
      <div className="footer-content container">
        {/* Brand Section */}
        <div className="footer-brand">
          <Link to="/" className="footer-logo">
            <span className="logo-text">{logoInitials}</span>
            <span>{siteName}</span>
          </Link>
          <p className="footer-tagline">{tagline}</p>
          
          {activeSocialLinks.length > 0 && (
            <div className="footer-social">
              {activeSocialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a 
                    key={social.platform}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`social-link social-${social.platform}`}
                    aria-label={social.label}
                  >
                    <IconComponent />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="footer-section">
          <h4>Navigation</h4>
          <ul className="footer-links">
            {quickLinks.map(link => (
              <li key={link.path}>
                <Link to={link.path}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Info */}
        <div className="footer-section">
          <h4>Contact</h4>
          <ul className="footer-contact">
            <li>
              <FiMail />
              <a href={`mailto:${email}`}>{email}</a>
            </li>
            {phones.map((phone, index) => (
              <li key={index}>
                <FiPhone />
                <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>
              </li>
            ))}
            <li>
              <FiMapPin />
              <span>{address}</span>
            </li>
          </ul>
        </div>

        {/* Newsletter or CTA */}
        <div className="footer-section">
          <h4>Restons connectés</h4>
          <p>Suivez-moi sur les réseaux sociaux pour ne rien manquer de mes actualités.</p>
          <Link to="/a-propos#contact" className="btn btn-outline btn-sm">
            Me contacter
          </Link>
        </div>
      </div>

      {/* Copyright & Credits */}
      <div className="footer-bottom">
        <div className="container">
          <p className="copyright">
            © {currentYear} {siteName}. Tous droits réservés.
          </p>
          
          <div className="footer-credits">
            <p className="made-with">
              Fait avec <FiHeart className="heart-icon" /> au Burkina Faso
            </p>
            <span className="credits-divider">•</span>
            <p className="developed-by">
              <FiCode className="code-icon" />
              Développé par{' '}
              <a 
                href="https://bjosue-myportfolio.netlify.app/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="developer-link"
              >
                J0su3Cod
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;